import { supabase } from './supabaseClient'

export type AccountProfile = {
  first_name: string | null
  last_name: string | null
  email: string | null
  dob: string | null
  sex: string | null
  timezone: string | null
  language_preference: string | null
  training_goal: string | null
  training_age: string | null
  created_at: string | null
}

export function displayFullName(profile: Pick<AccountProfile, 'first_name' | 'last_name'>): string {
  const parts = [profile.first_name?.trim(), profile.last_name?.trim()].filter(Boolean)
  return parts.join(' ') || ''
}

export function profileInitials(profile: Pick<AccountProfile, 'first_name' | 'last_name' | 'email'>): string {
  const first = profile.first_name?.trim()?.[0] ?? ''
  const last = profile.last_name?.trim()?.[0] ?? ''
  if (first || last) return `${first}${last}`.toUpperCase()
  const email = profile.email?.trim()?.[0]
  return email ? email.toUpperCase() : '?'
}

export function computeAgeFromDob(dob: string | null): number | null {
  if (!dob) return null
  const birth = new Date(`${dob}T12:00:00`)
  if (Number.isNaN(birth.getTime())) return null

  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDelta = today.getMonth() - birth.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) {
    age -= 1
  }
  return age >= 0 && age < 130 ? age : null
}

export function formatDob(dob: string | null, locale: string): string | null {
  if (!dob) return null
  const date = new Date(`${dob}T12:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date)
}

export function formatMemberSince(iso: string | null, locale: string): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date)
}

export async function loadAccountProfile(userId: string): Promise<{
  profile: AccountProfile | null
  error: string | null
}> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'first_name, last_name, email, dob, sex, timezone, language_preference, training_goal, training_age, created_at',
    )
    .eq('id', userId)
    .maybeSingle()

  if (error) return { profile: null, error: error.message }
  if (!data) return { profile: null, error: null }

  return { profile: data as AccountProfile, error: null }
}
